import React, { useEffect, useState } from "react";
import usersService from "../api/services/users.service";

/**
 * A user's profile photo, falling back to their initials.
 *
 * The avatar endpoint is authenticated, so the image cannot be loaded by
 * pointing an <img> at the URL — the browser would send no Bearer token and
 * get a 401. The bytes are fetched through the API client instead and wrapped
 * in an object URL, which is revoked on unmount to avoid leaking blobs.
 *
 * `version` (the user's avatar_updated_at) busts the cache when the photo is
 * replaced; without it a new upload would keep showing the old image.
 */
export default function UserAvatar({
  userId,
  hasAvatar,
  version,
  name = "",
  className = "",
}) {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    let revoked = false;
    let objectUrl = null;

    if (!userId || !hasAvatar) {
      setUrl(null);
      return undefined;
    }

    usersService.fetchAvatarUrl(userId).then((result) => {
      if (revoked) {
        // Unmounted mid-flight — release immediately rather than set state.
        if (result) URL.revokeObjectURL(result);
        return;
      }
      objectUrl = result;
      setUrl(result);
    });

    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [userId, hasAvatar, version]);

  if (url) {
    return (
      <img
        src={url}
        alt={name ? `${name}'s profile photo` : "Profile photo"}
        className={`rounded-full object-cover ${className}`}
      />
    );
  }

  const initials =
    name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "?";

  return (
    <div
      aria-label={name ? `${name}'s profile` : "Profile"}
      className={`rounded-full bg-[#173151] text-white flex items-center justify-center font-semibold font-manrope select-none ${className}`}
    >
      {initials}
    </div>
  );
}
